import React from 'react';
import { GDPPanel } from '../components/panels/macro/GDPPanel';
import { GrowthComparisonPanel } from '../components/panels/macro/GrowthComparisonPanel';
import { InflationPanel } from '../components/panels/macro/InflationPanel';
import { FiscalPanel } from '../components/panels/macro/FiscalPanel';
import { TradePanel } from '../components/panels/macro/TradePanel';
import { FXPanel } from '../components/panels/macro/FXPanel';
import { ForecastTable } from '../components/panels/macro/ForecastTable';
import { usePanelManager, PanelConfig } from '../hooks/usePanelManager';
import { MinimizedBar } from '../components/ui/MinimizedBar';

// Panel configuration
const panelConfig: PanelConfig[] = [
  { id: 'gdp', title: 'GDP', row: 1, colSpan: 2 },
  { id: 'growth', title: 'Growth Comparison', row: 1, colSpan: 2 },
  { id: 'inflation', title: 'Inflation', row: 2 },
  { id: 'fiscal', title: 'Fiscal', row: 2 },
  { id: 'trade', title: 'Trade', row: 2 },
  { id: 'fx', title: 'FX', row: 2 },
  { id: 'forecast', title: 'Forecast', row: 3, colSpan: 4 },
];

// Panel component map
const panelComponents: Record<string, React.FC<{ isMinimized?: boolean; onMinimize?: () => void }>> = {
  gdp: GDPPanel,
  growth: GrowthComparisonPanel,
  inflation: InflationPanel,
  fiscal: FiscalPanel,
  trade: TradePanel,
  fx: FXPanel,
  forecast: ForecastTable,
};

const MacroView: React.FC = () => {
  const {
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  } = usePanelManager(panelConfig);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <MinimizedBar panels={minimizedPanelsList} onRestore={handleRestore} />

      <div className="flex-1 flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 md:grid-rows-[1fr_1fr_auto] gap-px p-px bg-terminal-border overflow-y-auto md:overflow-hidden">
        {/* Row 1 - GDP & Growth Comparison */}
        {getOrderedPanels(1).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 2 - Inflation, Fiscal, Trade, FX */}
        {getOrderedPanels(2).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 3 - Forecast Table */}
        {getOrderedPanels(3).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[200px] sm:min-h-[250px] md:min-h-0 col-span-full h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MacroView;
